import React from 'react';
import type { SchoolCode } from '../types';
import { formatGradeDisplay, getGradeFromMarks, getRemark } from '../utils/msceGrading';
import { formatLetterGradeDisplay, letterFromMarks, remarkFromLetter, usesLetterGrading } from '../utils/letterGrading';
import { REPORT_CARD_ACADEMIC_MOTTO, SHARED_SCHOOL_MOTTO } from '../schoolBranding';
import { formatClassLevelLabel } from '../utils/classLevelLabel';
import styles from './ReportCard.module.css';

interface Grade {
  subject: string;
  subject_code: string;
  marks: number;
  grade: string;
  position: number | null;
  remarks: string;
  teacher_remarks?: string;
  teacher_name?: string;
}

interface Student {
  id: number;
  name: string;
  student_id: string;
  form: number;
  school: string;
}

interface Statistics {
  grading_mode?: 'letter' | 'msce';
  total_marks: number;
  average_marks: number;
  position_in_class: number | null;
  total_students: number;
  subject_count: number;
  best_6_count?: number;
  overall_msce_grade: number | null;
  overall_remark: string | null;
  overall_letter?: string | null;
  overall_letter_remark?: string | null;
}

interface Settings {
  term: number;
  year: number;
  term_start_date?: string;
  term_end_date?: string;
  next_term_opening?: string;
  date_of_release?: string;
  borders_fee?: string;
  day_scholar_fee?: string;
  principal_remarks?: string;
  grading_scale?: Array<{ position: number | string; range: string; remark?: string }>;
}

interface ReportCardData {
  student: Student;
  grades: Grade[];
  statistics: Statistics;
  settings: Settings;
  remarks?: {
    teacher: string;
    principal: string;
  };
}

interface ReportCardProps {
  data: ReportCardData;
}

export const ReportCard: React.FC<ReportCardProps> = ({ data }) => {
  const { student, grades, statistics, settings, remarks } = data;

  const schoolLogo = student.school === 'KBA' ? '/kba logo.PNG' : '/tlc logo.jpg';
  const schoolName = student.school === 'KBA' ? 'Kingdom Builder Academy' : 'The Learning Centre';
  const schoolCode = student.school as SchoolCode;
  const letterMode =
    statistics.grading_mode === 'letter' ||
    usesLetterGrading(schoolCode, student.form);

  const formatGradingScale = () => {
    if (!settings.grading_scale) return '';
    return settings.grading_scale
      .map((scale) => {
        const r = scale.remark ? ` ${scale.remark}` : '';
        return `${scale.position}=(${scale.range})${r}`;
      })
      .join(', ');
  };

  const formatSubjectGrade = (g: Grade) => {
    if (letterMode) {
      const letter = /^(A\*|[A-F])$/.test(g.grade) ? g.grade : letterFromMarks(g.marks);
      return formatLetterGradeDisplay(letter);
    }
    const n = parseInt(g.grade, 10);
    if (!Number.isNaN(n) && n >= 1 && n <= 9) {
      return formatGradeDisplay(n, getRemark(n));
    }
    const { grade, remark } = getGradeFromMarks(g.marks);
    return formatGradeDisplay(grade, remark);
  };

  return (
    <div className={styles.reportCard}>
      {/* Header */}
      <div className={styles.header}>
        <img src={schoolLogo} alt={`${student.school} Logo`} className={styles.logo} />
        <h1 className={styles.schoolName}>{schoolName}</h1>
        <p className={styles.motto}>{SHARED_SCHOOL_MOTTO}</p>
      </div>

      <h2 className={styles.title}>STUDENT PROGRESS REPORT</h2>

      {/* Student Info */}
      <div className={styles.infoSection}>
        <div className="tableScroll">
          <table className={styles.infoTable}>
            <tbody>
              <tr>
                <td className={styles.labelCell}>
                  <strong>Examinations number:</strong> {student.student_id}
                </td>
                <td className={styles.labelCell}>
                  <strong>Position in class:</strong>{' '}
                  {statistics.position_in_class != null ? statistics.position_in_class : '-'}
                </td>
              </tr>
              <tr>
                <td className={styles.labelCell}>
                  <strong>Student name:</strong> {student.name}
                </td>
                <td className={styles.labelCell}>
                  <strong>Out of:</strong>{' '}
                  {statistics.total_students != null ? statistics.total_students : '-'}
                </td>
              </tr>
              <tr>
                <td className={styles.labelCell}>
                  <strong>Class:</strong>{' '}
                  {formatClassLevelLabel(student.school as SchoolCode, student.form)}
                </td>
                <td className={styles.labelCell}>
                  <strong>Term / year:</strong> {settings.term} / {settings.year}
                  {' · '}
                  <strong>Date of Release:</strong> {settings.date_of_release || '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Grades Table */}
      <div className={styles.gradesSection}>
        <div className="tableScroll">
          <table className={styles.gradesTable}>
            <thead>
              <tr>
                <th>SUBJECT</th>
                <th>MARKS</th>
                <th>{letterMode ? 'GRADE' : 'POINTS'}</th>
                <th>POSITION</th>
                <th>TEACHER REMARKS</th>
                <th>SIGN</th>
              </tr>
            </thead>
            <tbody>
              {grades.length > 0 ? (
                grades.map((grade, index) => (
                  <tr key={index}>
                    <td className={styles.subjectCell}>{grade.subject.toUpperCase()}</td>
                    <td className={styles.centerCell}>{grade.marks}</td>
                    <td className={styles.centerCell}>{formatSubjectGrade(grade)}</td>
                    <td className={styles.centerCell}>{grade.position ?? '-'}</td>
                    <td className={styles.remarksCell}>{grade.remarks}</td>
                    <td className={styles.signCell}>{grade.teacher_name || ''}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className={styles.centerCell} style={{ padding: 4, color: '#6b7280' }}>
                    No grades available for this term
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Overall grade / points */}
      <div className={styles.aggregateSection}>
        {letterMode ? (
          <>
            <strong>Overall grade (from mean of subject marks):</strong>{' '}
            {statistics.overall_letter
              ? `${statistics.overall_letter} — ${statistics.overall_letter_remark ?? remarkFromLetter(statistics.overall_letter)}`
              : '—'}
          </>
        ) : (
          <>
            <strong>Overall Points (mean of subject points):</strong>{' '}
            {statistics.overall_msce_grade != null && statistics.overall_remark
              ? formatGradeDisplay(statistics.overall_msce_grade, statistics.overall_remark)
              : '—'}
          </>
        )}
        <span className={styles.gradingScale}>
          {formatGradingScale()}
        </span>
      </div>

      {/* Teacher's Remarks */}
      <div className={styles.remarksBox}>
        <strong>Form teacher's remarks:</strong>
        <div className={styles.remarksContent}>
          {remarks?.teacher || ''}
        </div>
      </div>

      <div className={styles.signatureBox}>
        <strong>Form teacher's signature:</strong> __________________
      </div>

      {/* Principal's Remarks */}
      <div className={styles.remarksBox}>
        <strong>Principal's remarks:</strong>
        <div className={styles.remarksContent}>
          {remarks?.principal || settings.principal_remarks || ''}
        </div>
      </div>

      <div className={styles.signatureBox}>
        <strong>Signature:</strong> __________________
      </div>

      {/* Academic Recommendation */}
      <div className={styles.recommendationSection}>
        <h3 className={styles.recommendationTitle}>Academic Recommendation (Tick one)</h3>
        <div className={styles.checkboxGroup}>
          <div className={styles.checkbox}>
            <span>&#9744;</span> Pass and proceed
          </div>
          <div className={styles.checkbox}>
            <span>&#9744;</span> Fail and repeat
          </div>
          <div className={styles.checkbox}>
            <span>&#9744;</span> Fail and proceed but to register as an external candidate
          </div>
        </div>
      </div>

      {/* Fee Structure */}
      <div className={styles.feeSection}>
        <h3 className={styles.feeTitle}>FEES STRUCTURE</h3>
        <div className="tableScroll">
          <table className={styles.feeTable}>
            <tbody>
              <tr>
                <td className={styles.feeLabel}>BORDERS</td>
                <td className={styles.feeAmount}>
                  MK{settings.borders_fee || '69,696.00'}
                </td>
              </tr>
              <tr>
                <td className={styles.feeLabel}>DAY SCHOLAR</td>
                <td className={styles.feeAmount}>
                  MK{settings.day_scholar_fee || '195,900.00'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Next Term Opening */}
      <div className={styles.footer}>
        <strong>Next term opens on {settings.next_term_opening || '__________________'}</strong>
      </div>

      <p className={styles.academicAttitudeMotto}>{REPORT_CARD_ACADEMIC_MOTTO}</p>
    </div>
  );
};
